import { type Options, type UseQueryStatesKeysMap, type UseQueryStatesReturn, useQueryStates } from "nuqs";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useLocation, useMatches, useSearchParams } from "react-router";

type SessionOptions = Options & { bucket?: string };

export function useQueryStatesWithSessionStorage<T extends UseQueryStatesKeysMap>(
    parsers: T,
    { bucket, ...nuqsOptions }: SessionOptions = {},
): UseQueryStatesReturn<T> {
    const [values, setValues] = useQueryStates(parsers, nuqsOptions);

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchString = searchParams.toString();

    const routeKey = storageKey(useStableRouteKey(), bucket);

    useLayoutEffect(() => {
        if (searchString !== "") {
            // URL is the latest intent — persist it and never revert edits.
            writeToSessionStorage(routeKey, searchParams);
            return;
        }

        // Empty URL: restore the latest stored value, if any.
        const latest = readFromSessionStorage(routeKey);
        if (latest !== null && latest !== "") {
            setSearchParams(latest, { replace: true, state: location.state });
        }
    }, [searchString, location, routeKey, searchParams, setSearchParams]);

    const setValuesAndPersist = useCallback<typeof setValues>(
        async (...args) => {
            const params = await setValues(...args);
            writeToSessionStorage(routeKey, params);
            return params;
        },
        [setValues, routeKey],
    );

    return [values, setValuesAndPersist];
}

function useStableRouteKey(): string {
    const matches = useMatches();

    return useMemo(() => matches[matches.length - 1]?.id ?? "", [matches]);
}

function storageKey(routeKey: string, bucket?: string) {
    return bucket !== undefined && bucket !== "" ? `params:${routeKey}:${bucket}` : `params:${routeKey}`;
}

function readFromSessionStorage(routeKey: string) {
    return sessionStorage.getItem(routeKey);
}

function writeToSessionStorage(routeKey: string, params: URLSearchParams) {
    sessionStorage.setItem(routeKey, params.toString());
}
